import React, { useMemo, useState } from "react";
import { Button, Input, InputNumber, Space, Table, Tag, Tooltip, message } from "antd";
import { PrinterOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { isLoaded } from "react-redux-firebase";
import * as dayjs from "dayjs";
import { ListCard, Num, PageHeader } from "components/shared-components/purchase";
import { useProductColors } from "../../../../data/hooks/useProductColors";
import { generateStickerPdf, isValidEan13, toStickerProduct } from "../../../../functions/stickers";

/**
 * Losse stickerprint (BMX-10).
 *
 * Tot nu toe kwamen stickers alleen uit het leveranciersportaal, en dan altijd
 * voor een hele inkooporder. Dat dekt niet het geval waarin een doos bij
 * binnenkomst de verkeerde sticker blijkt te hebben: dan wil je hier een paar
 * losse stickers bij kunnen printen. Vandaar: elk product, eigen aantallen per
 * verpakkingssoort.
 *
 * Inner en master zijn aparte aantallen omdat het meestal om één van de twee
 * gaat. Het aantal stuks per doos (het PCS-getal op de sticker) komt van het
 * product zelf; staat dat er niet op, dan is er geen sticker te maken en blijft
 * het veld leeg.
 *
 * Het renderen zelf staat in functions/stickers.js — gedeelde port met het
 * portaal, zodat beide dezelfde sticker opleveren.
 */
const Stickers = () => {
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const products = useSelector((state) => state.firestoreReducer.ordered.products);
  const { colors: productColors } = useProductColors();

  const [search, setSearch] = useState("");
  const [counts, setCounts] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);

  const colorNameById = useMemo(() => {
    const map = {};
    (productColors || []).forEach((color) => {
      map[color.id] = color.name;
    });
    return map;
  }, [productColors]);

  const setCount = (productId, variant, value) => {
    setCounts((prev) => ({
      ...prev,
      [productId]: {
        inner: prev[productId]?.inner || 0,
        master: prev[productId]?.master || 0,
        [variant]: value > 0 ? value : 0,
      },
    }));
  };

  const filtered = useMemo(() => {
    const list = products || [];
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter((product) =>
      [product.name, product.code, product.barcode]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(term)),
    );
  }, [products, search]);

  const jobs = useMemo(() => {
    const result = [];
    (products || []).forEach((product) => {
      const entry = counts[product.id];
      if (!entry) return;

      const stickerProduct = toStickerProduct(product, colorNameById[product.colorNew]);

      if (entry.inner > 0 && product.innerPackagingQuantity) {
        result.push({
          product: stickerProduct,
          variant: "inner",
          quantity: product.innerPackagingQuantity,
          copies: entry.inner,
        });
      }
      if (entry.master > 0 && product.masterPackagingQuantity) {
        result.push({
          product: stickerProduct,
          variant: "master",
          quantity: product.masterPackagingQuantity,
          copies: entry.master,
        });
      }
    });
    return result;
  }, [products, counts, colorNameById]);

  const totalStickers = jobs.reduce((sum, job) => sum + job.copies, 0);
  const selectedProducts = new Set(jobs.map((job) => job.product.itemNo)).size;

  const handleDownload = async () => {
    setIsGenerating(true);
    const hide = messageApi.loading(t("stickers.generating"), 0);
    try {
      const { pages, failed, withoutBarcode } = await generateStickerPdf(
        jobs,
        `Stickers_${dayjs().format("YYYY-MM-DD")}.pdf`,
      );

      hide();

      if (pages === 0) {
        messageApi.error(t("stickers.failed_all"));
        return;
      }

      messageApi.success(t("stickers.downloaded", { stickers: pages }));

      if (withoutBarcode.length > 0) {
        messageApi.warning(t("stickers.without_barcode", { products: withoutBarcode.join(", ") }), 10);
      }
      if (failed.length > 0) {
        messageApi.error(t("stickers.failed_some", { products: failed.map((f) => f.name).join(", ") }), 10);
      }
    } catch (error) {
      hide();
      console.error("Stickers genereren mislukt:", error);
      messageApi.error(t("stickers.failed_all"));
    } finally {
      setIsGenerating(false);
    }
  };

  const quantityColumn = (variant, packagingField) => ({
    title: t(`stickers.${variant}`),
    key: variant,
    width: 170,
    render: (_, record) =>
      record[packagingField] ? (
        <Space size={4}>
          <InputNumber
            min={0}
            max={999}
            value={counts[record.id]?.[variant] || 0}
            onChange={(value) => setCount(record.id, variant, value)}
            style={{ width: 70 }}
          />
          <span style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.45)" }}>
            × <Num>{record[packagingField]}</Num> {t("stickers.pcs")}
          </span>
        </Space>
      ) : (
        <Tooltip title={t("stickers.no_packaging_quantity")}>
          <span style={{ color: "rgba(0, 0, 0, 0.25)" }}>—</span>
        </Tooltip>
      ),
  });

  const columns = [
    {
      title: t("product"),
      dataIndex: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <span>{name}</span>
          {!isValidEan13(record.barcode) ? (
            <Tag color="orange" style={{ marginTop: 4 }}>
              {t("stickers.no_barcode_tag")}
            </Tag>
          ) : null}
        </Space>
      ),
    },
    {
      title: t("code"),
      dataIndex: "code",
      width: 140,
      render: (code) => (code ? <Num>{code}</Num> : "-"),
    },
    {
      title: t("ean"),
      dataIndex: "barcode",
      width: 160,
      render: (barcode) => (barcode ? <Num>{barcode}</Num> : "-"),
    },
    quantityColumn("inner", "innerPackagingQuantity"),
    quantityColumn("master", "masterPackagingQuantity"),
  ];

  return (
    <>
      {contextHolder}
      <PageHeader
        title={null}
        subtitle={t("stickers.subtitle")}
        extra={
          <Space>
            <span style={{ fontSize: 13, color: "rgba(0, 0, 0, 0.55)" }}>
              {totalStickers > 0
                ? t("stickers.selection_summary", {
                    stickers: totalStickers,
                    products: selectedProducts,
                  })
                : t("stickers.nothing_selected")}
            </span>
            <Button onClick={() => setCounts({})} disabled={totalStickers === 0}>
              {t("stickers.clear")}
            </Button>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handleDownload}
              loading={isGenerating}
              disabled={totalStickers === 0}
            >
              {t("stickers.download")}
            </Button>
          </Space>
        }
      />

      <ListCard
        filters={
          <Input
            allowClear
            placeholder={t("stickers.search_placeholder")}
            prefix={<SearchOutlined />}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            style={{ maxWidth: 320 }}
          />
        }
        footnote={t("stickers.footnote")}
      >
        <Table
          rowKey="id"
          loading={!isLoaded(products)}
          dataSource={filtered}
          columns={columns}
          pagination={{ pageSize: 25, showSizeChanger: true }}
        />
      </ListCard>
    </>
  );
};

export default Stickers;
